import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { RefreshCw } from "lucide-react";

const REFRESH_INTERVAL = 5000;

const STATUS_META = {
  OPEN:      { color: "#22c55e", bg: "#14532d20", icon: "🟢", label: "OPEN"      },
  COMPLETE:  { color: "#3b82f6", bg: "#1e3a8a20", icon: "✅", label: "COMPLETE"  },
  CANCELLED: { color: "#94a3b8", bg: "#33415520", icon: "⊗",  label: "CANCELLED" },
  REJECTED:  { color: "#ef4444", bg: "#7f1d1d20", icon: "🔴", label: "REJECTED"  },
};

const SORT_OPTIONS = [
  { value: "time",     label: "Time"     },
  { value: "username", label: "Username" },
  { value: "userId",   label: "User ID"  },
  { value: "broker",   label: "Broker"   },
  { value: "symbol",   label: "Symbol"   },
  { value: "status",   label: "Status"   },
];

const CLIENT_ID_COLORS = [
  "#ff2d55", // velvet red
  "#00d97e", // velvet green
  "#ffd60a", // velvet yellow
  "#bf5af2", // velvet purple
  "#00c7ff", // velvet blue
  "#ff8c00", // velvet orange
  "#ff4fd8", // velvet pink
  "#64d2ff", // velvet cyan
];

function getClientIdColor(clientId) {
  const value = String(clientId || "");
  let hash = 0;

  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }

  return CLIENT_ID_COLORS[Math.abs(hash) % CLIENT_ID_COLORS.length];
}

function normalizeStatus(raw) {
  if (!raw) return "OPEN";
  const s = raw.toUpperCase();
  if (s === "TRADED" || s.includes("COMPLETE") || s.includes("FILLED") || s.includes("PART_TRADED")) return "COMPLETE";
  if (s.includes("CANCEL")) return "CANCELLED";
  if (s.includes("REJECT")) return "REJECTED";
  if (s.includes("TRANSIT") || s.includes("PENDING") || s.includes("OPEN")) return "OPEN";
  return "OPEN";
}

function normalizeDhanOrder(o, acc) {
  return {
    orderId:      o.orderId            || "",
    time:         o.createTime         || o.updateTime   || new Date().toISOString(),
    userId:       acc.id,
    username:     acc.name             || acc.id,
    broker:       "Dhan",
    symbol:       o.tradingSymbol      || o.securityName || "—",
    status:       normalizeStatus(o.orderStatus),
    type:         o.transactionType    || "—",
    orderType:    o.orderType          || "—",
    qty:          o.quantity           || 0,
    price:        o.orderPrice         || o.price        || 0,
    triggerPrice: o.triggerPrice       || 0,
    avgPrice:     o.averageTradedPrice || 0,
    remarks:      o.omsErrorDescription || o.drvExpiryDate || "—",
  };
}

function normalizeUpstoxOrder(o, acc) {
  return {
    orderId:      o.order_id          || "",
    time:         o.order_timestamp   || o.exchange_timestamp || new Date().toISOString(),
    userId:       acc.id,
    username:     acc.name            || acc.id,
    broker:       "Upstox",
    symbol:       o.trading_symbol    || o.instrument_token   || "—",
    status:       normalizeStatus(o.status),
    type:         o.transaction_type  || "—",
    orderType:    o.order_type        || "—",
    qty:          o.quantity          || 0,
    price:        o.price             || 0,
    triggerPrice: o.trigger_price     || 0,
    avgPrice:     o.average_price     || 0,
    remarks:      o.status_message    || o.tag || "—",
  };
}

function normalizeZerodhaOrder(o, acc) {
  return {
    orderId:      o.order_id          || "",
    time:         o.order_timestamp   || o.exchange_timestamp || new Date().toISOString(),
    userId:       acc.id,
    username:     acc.name            || acc.id,
    broker:       "Zerodha",
    symbol:       o.tradingsymbol     || "—",
    status:       normalizeStatus(o.status),
    type:         o.transaction_type  || "—",
    orderType:    o.order_type        || "—",
    qty:          o.quantity          || 0,
    price:        o.price             || 0,
    triggerPrice: o.trigger_price     || 0,
    avgPrice:     o.average_price     || 0,
    remarks:      o.status_message    || o.tag || "—",
  };
}

async function fetchDhanOrders(accessToken, clientId) {
  try {
    const res = await fetch("/api/orders/dhan", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken, clientId }),
    });
    const data = await res.json();
    return data.orders || [];
  } catch { return []; }
}

async function fetchUpstoxOrders(accessToken) {
  try {
    const res = await fetch("/api/orders/upstox", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken }),
    });
    const data = await res.json();
    return data.orders || [];
  } catch { return []; }
}

async function fetchZerodhaOrders(apiKey, accessToken) {
  try {
    const res = await fetch("/api/orders/zerodha", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ apiKey, accessToken }),
    });
    const data = await res.json();
    return data.orders || [];
  } catch { return []; }
}

export default function Orders() {
  const [accounts, setAccounts]               = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("ALL");
  const [orders, setOrders]                   = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [lastRefreshed, setLastRefreshed]     = useState(null);
  const [wsStatus, setWsStatus]               = useState("disconnected");
  const [search, setSearch]                   = useState("");
  const [sortBy, setSortBy]                   = useState("time");
  const [sortDir, setSortDir]                 = useState("desc");
  const [filters, setFilters]                 = useState({
    ALL: false, OPEN: true, COMPLETE: true, CANCELLED: false, REJECTED: true,
  });
  const [autoRefresh, setAutoRefresh]         = useState(true);
  const [selectedOrder, setSelectedOrder]     = useState(null);
  const [modifyForm, setModifyForm]           = useState({
    orderType: "LIMIT",
    quantity: "",
    price: "",
    triggerPrice: "",
    validity: "DAY",
  });
  const [actionLoading, setActionLoading]     = useState(false);
  const [actionMessage, setActionMessage]     = useState("");

  const intervalRef  = useRef(null);
  const wsRef        = useRef(null);
  const reconnectRef = useRef(null);
  const ordersMapRef = useRef(new Map());
  // Keep a ref to accounts so WS callbacks always see latest value
  const accountsRef  = useRef([]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("ct_accounts")) || [];
      setAccounts(stored);
      accountsRef.current = stored;
    } catch {
      setAccounts([]);
      accountsRef.current = [];
    }
  }, []);

  // Keep accountsRef in sync
  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  const upsertOrder = useCallback((normalized) => {
    if (!normalized.orderId) return;
    ordersMapRef.current.set(normalized.orderId, normalized);
    setOrders(Array.from(ordersMapRef.current.values()));
    setLastRefreshed(new Date());
  }, []);

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    try {
      const accsToFetch = selectedAccount === "ALL"
        ? accountsRef.current
        : accountsRef.current.filter(a => a.id === selectedAccount);
      const nextMap = new Map(ordersMapRef.current);

      for (const acc of accsToFetch) {
        if (acc.broker === "Dhan" && acc.accessToken) {
          const raw = await fetchDhanOrders(acc.accessToken, acc.id);
          raw.forEach(o => {
            const normalized = normalizeDhanOrder(o, acc);
            if (normalized.orderId) nextMap.set(normalized.orderId, normalized);
          });
        }
        if (acc.broker === "Upstox" && acc.accessToken) {
          const raw = await fetchUpstoxOrders(acc.accessToken);
          raw.forEach(o => {
            const normalized = normalizeUpstoxOrder(o, acc);
            if (normalized.orderId) nextMap.set(normalized.orderId, normalized);
          });
        }
        if (acc.broker === "Zerodha" && acc.accessToken && acc.apiKey) {
          const raw = await fetchZerodhaOrders(acc.apiKey, acc.accessToken);
          raw.forEach(o => {
            const normalized = normalizeZerodhaOrder(o, acc);
            if (normalized.orderId) nextMap.set(normalized.orderId, normalized);
          });
        }
      }

      ordersMapRef.current = nextMap;
      setOrders(Array.from(nextMap.values()));
      setLastRefreshed(new Date());
    } catch (err) {
      console.error("Snapshot fetch error:", err);
    }
    setLoading(false);
  }, [selectedAccount]);

  // ── WebSocket connection — FIX: only send after onopen ───────
  const connectWS = useCallback(() => {
    // Don't reconnect if already open or connecting
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
       wsRef.current.readyState === WebSocket.CONNECTING)
    ) return;

    clearTimeout(reconnectRef.current);

    const ws = new WebSocket(
      `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/ws`
    );
    wsRef.current = ws;

    ws.onopen = () => {
      setWsStatus("connected");
      console.log("Orders WS connected");

      // FIX: send subscribe ONLY inside onopen — never before
      const dhanAccounts = accountsRef.current
        .filter(a => a.broker === "Dhan" && a.accessToken)
        .map(a => ({ clientId: a.id, accessToken: a.accessToken }));

      if (dhanAccounts.length > 0) {
        ws.send(JSON.stringify({ action: "subscribe", accounts: dhanAccounts }));
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "SUBSCRIBED") {
          console.log("WS subscribed to:", msg.clientIds);
          return;
        }
        if (msg.type === "ORDER_UPDATE") {
          const acc = accountsRef.current.find(a => a.id === msg.clientId)
            || { id: msg.clientId, name: msg.clientId };
          upsertOrder({
            orderId:      msg.orderId      || "",
            time:         msg.time         || new Date().toISOString(),
            userId:       acc.id,
            username:     acc.name         || acc.id,
            broker:       "Dhan",
            symbol:       msg.symbol       || "—",
            status:       normalizeStatus(msg.status),
            type:         msg.txnType      || "—",
            orderType:    msg.orderType    || "—",
            qty:          msg.qty          || 0,
            price:        msg.price        || 0,
            triggerPrice: msg.triggerPrice || 0,
            avgPrice:     msg.avgPrice     || 0,
            remarks:      msg.remarks      || "—",
          });
        }
      } catch (e) {
        console.error("WS message parse error:", e);
      }
    };

    ws.onerror = (err) => {
      console.error("Orders WS error:", err);
      setWsStatus("error");
    };

    ws.onclose = () => {
      console.log("Orders WS closed");
      setWsStatus("disconnected");
      wsRef.current = null;
      // Auto-reconnect after 5s
      if (autoRefresh) {
        reconnectRef.current = setTimeout(connectWS, 5000);
      }
    };
  }, [autoRefresh, upsertOrder]);

  // ── Cleanup WS on unmount ─────────────────────────────────────
  // FIX: check readyState before calling send in cleanup
  useEffect(() => {
    return () => {
      clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: "unsubscribe" }));
        }
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  // ── On accounts/selectedAccount change ───────────────────────
  useEffect(() => {
    if (accounts.length === 0) return;
    ordersMapRef.current = new Map();
    setOrders([]);
    fetchSnapshot();
    if (autoRefresh) connectWS();
  }, [accounts, selectedAccount]);

  // ── Auto REST refresh every 5s ────────────────────────────────
  useEffect(() => {
    clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchSnapshot, REFRESH_INTERVAL);
    }
    return () => clearInterval(intervalRef.current);
  }, [fetchSnapshot, autoRefresh]);

  // ── Toggle autoRefresh ────────────────────────────────────────
  useEffect(() => {
    if (autoRefresh) {
      connectWS();
    } else {
      clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: "unsubscribe" }));
        }
        wsRef.current.close();
        wsRef.current = null;
      }
    }
  }, [autoRefresh]);

  const toggleAll = () => {
    const anyUnchecked = !Object.entries(filters)
      .filter(([k]) => k !== "ALL")
      .every(([, v]) => v);
    setFilters({ ALL: false, OPEN: anyUnchecked, COMPLETE: anyUnchecked, CANCELLED: anyUnchecked, REJECTED: anyUnchecked });
  };

  const toggleFilter = (key) => setFilters(f => ({ ...f, [key]: !f[key] }));

  const filteredOrders = useMemo(() => {
    let data = selectedAccount === "ALL"
      ? [...orders]
      : orders.filter(o => o.userId === selectedAccount);

    const anyOn = Object.entries(filters).filter(([k]) => k !== "ALL").some(([, v]) => v);
    if (anyOn) data = data.filter(o => filters[o.status]);

    if (search.trim()) {
      const s = search.toLowerCase();
      data = data.filter(o =>
        o.symbol?.toLowerCase().includes(s)   ||
        o.userId?.toLowerCase().includes(s)   ||
        o.username?.toLowerCase().includes(s) ||
        o.broker?.toLowerCase().includes(s)   ||
        o.orderId?.toLowerCase().includes(s)  ||
        o.status?.toLowerCase().includes(s)
      );
    }

    data.sort((a, b) => {
      let va = sortBy === "time" ? new Date(a.time) : (a[sortBy] || "");
      let vb = sortBy === "time" ? new Date(b.time) : (b[sortBy] || "");
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      return 0;
    });

    return data;
  }, [orders, filters, search, sortBy, sortDir, selectedAccount]);

  const stats = useMemo(() => ({
    total:     orders.length,
    open:      orders.filter(o => o.status === "OPEN").length,
    complete:  orders.filter(o => o.status === "COMPLETE").length,
    cancelled: orders.filter(o => o.status === "CANCELLED").length,
    rejected:  orders.filter(o => o.status === "REJECTED").length,
  }), [orders]);

  const inp = {
    background: "#1e293b", border: "1px solid #334155", color: "#e2e8f0",
    borderRadius: 6, padding: "8px 12px", fontSize: 13, outline: "none",
  };

  const statCards = [
    { label: "Total Orders", value: stats.total,     icon: "📋", iconBg: "#3b82f6" },
    { label: "Open",         value: stats.open,      icon: "🕒", iconBg: "#22c55e", color: "#22c55e" },
    { label: "Completed",    value: stats.complete,  icon: "✅", iconBg: "#3b82f6", color: "#3b82f6" },
    { label: "Cancelled",    value: stats.cancelled, icon: "⊗",  iconBg: "#334155", color: "#94a3b8" },
    { label: "Rejected",     value: stats.rejected,  icon: "🔴", iconBg: "#ef4444", color: "#ef4444" },
  ];

  const wsColor = wsStatus === "connected" ? "#22c55e" : wsStatus === "error" ? "#ef4444" : "#f59e0b";
  const wsLabel = wsStatus === "connected" ? "● LIVE" : wsStatus === "error" ? "● WS ERROR" : "○ CONNECTING...";
  const selectedOrderAccount = selectedOrder
    ? accounts.find(acc => String(acc.id) === String(selectedOrder.userId))
    : null;

  const openOrderView = (order) => {
    setSelectedOrder(order);
    setModifyForm({
      orderType: order.orderType && order.orderType !== "—" ? order.orderType : "LIMIT",
      quantity: String(order.qty || ""),
      price: String(order.price || 0),
      triggerPrice: String(order.triggerPrice || 0),
      validity: "DAY",
    });
    setActionMessage("");
  };

  const closeOrderView = () => {
    setSelectedOrder(null);
    setActionMessage("");
  };

  const buildDhanActionBody = () => ({
    accessToken: selectedOrderAccount?.accessToken || "",
    clientId: selectedOrder?.userId || "",
    orderId: selectedOrder?.orderId || "",
    orderType: modifyForm.orderType,
    quantity: Number(modifyForm.quantity || selectedOrder?.qty || 0),
    price: Number(modifyForm.orderType === "MARKET" ? 0 : modifyForm.price || 0),
    triggerPrice: Number(
      ["STOP_LOSS", "STOP_LOSS_MARKET"].includes(modifyForm.orderType)
        ? modifyForm.triggerPrice || 0
        : 0
    ),
    validity: modifyForm.validity || "DAY",
    disclosedQuantity: 0,
    proxyHost: selectedOrderAccount?.proxyHost || "",
    proxyPort: selectedOrderAccount?.proxyPort || "443",
    proxyUser: selectedOrderAccount?.proxyUser || "",
    proxyPass: selectedOrderAccount?.proxyPass || "",
  });

  const modifySelectedOrder = async () => {
    if (!selectedOrder || !selectedOrderAccount) return;
    setActionLoading(true);
    setActionMessage("");
    try {
      const res = await fetch("/api/orders/dhan/modify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildDhanActionBody()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || "Modify failed");
      setActionMessage("Modify request sent. Waiting for order update...");
      fetchSnapshot();
      closeOrderView();
    } catch (error) {
      setActionMessage(error.message || "Modify failed");
    } finally {
      setActionLoading(false);
    }
  };

  const cancelSelectedOrder = async () => {
    if (!selectedOrder || !selectedOrderAccount) return;
    if (!window.confirm(`Cancel order ${selectedOrder.orderId}?`)) return;
    setActionLoading(true);
    setActionMessage("");
    try {
      const res = await fetch("/api/orders/dhan/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildDhanActionBody()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || "Cancel failed");
      setActionMessage("Cancel request sent. Waiting for order update...");
      fetchSnapshot();
    } catch (error) {
      setActionMessage(error.message || "Cancel failed");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="mobile-page mobile-orders" style={{ padding: 24, color: "#e2e8f0", minHeight: "100vh", background: "#0f172a" }}>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 22 }}>📋</span>
          <h2 style={{ fontSize: 24, fontWeight: "bold", margin: 0 }}>Order Book</h2>
          {loading && <span style={{ fontSize: 11, color: "#f59e0b", letterSpacing: 2 }}>⟳ LOADING...</span>}
          {lastRefreshed && !loading && (
            <span style={{ fontSize: 11, color: "#475569" }}>Updated {lastRefreshed.toLocaleTimeString()}</span>
          )}
          <span style={{ fontSize: 11, color: wsColor, letterSpacing: 1, fontWeight: "bold" }}>{wsLabel}</span>
        </div>
        <button
          onClick={fetchSnapshot}
          className="btn-solid-green"
          style={{
            padding: "9px 18px",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {/* Account select + auto refresh */}
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: "14px 18px", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, color: "#94a3b8", whiteSpace: "nowrap" }}>Select Account:</label>
          <select
            value={selectedAccount}
            onChange={e => setSelectedAccount(e.target.value)}
            style={{ ...inp, flex: 1, minWidth: 220, maxWidth: 400 }}
          >
            <option value="ALL">All Accounts</option>
            {accounts.map(acc => (
              <option key={acc.id} value={acc.id}>
                {acc.name || acc.id} ({acc.id}) — {acc.broker}
              </option>
            ))}
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox" checked={autoRefresh}
              onChange={() => setAutoRefresh(v => !v)}
              style={{ width: 15, height: 15, accentColor: "#3b82f6" }}
            />
            <span style={{ color: "#94a3b8" }}>Auto-refresh (5s) + Live WS</span>
          </label>
        </div>
      </div>

      {/* Search + sort */}
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: "12px 18px", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#475569", fontSize: 14 }}>🔍</span>
            <input
              placeholder="Search symbol, username, broker, order ID..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ ...inp, width: "100%", paddingLeft: 32, boxSizing: "border-box" }}
            />
          </div>
          <label style={{ fontSize: 13, color: "#94a3b8", whiteSpace: "nowrap" }}>Sort by:</label>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...inp, minWidth: 120 }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
            style={{ ...inp, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontWeight: "bold", minWidth: 72, justifyContent: "center" }}
          >
            {sortDir === "asc" ? "↑" : "↓"} {sortDir.toUpperCase()}
          </button>
        </div>
      </div>

      {/* Status filters */}
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: "12px 18px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "#94a3b8" }}>Filter by Status:</span>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, fontWeight: "bold" }}>
            <input type="checkbox"
              checked={Object.entries(filters).filter(([k]) => k !== "ALL").every(([, v]) => v)}
              onChange={toggleAll} style={{ width: 15, height: 15, accentColor: "#3b82f6" }} />
            ALL
          </label>
          {Object.keys(STATUS_META).map(key => {
            const meta = STATUS_META[key];
            return (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                <input type="checkbox" checked={filters[key] || false}
                  onChange={() => toggleFilter(key)} style={{ width: 15, height: 15, accentColor: meta.color }} />
                <span style={{ color: meta.color, fontWeight: "bold" }}>{meta.icon} {meta.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Stat cards */}
      <div className="mobile-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 20 }}>
        {statCards.map(card => (
          <div key={card.label} style={{
            background: "#1e293b", border: "1px solid #334155", borderRadius: 10,
            padding: "16px 18px", display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>{card.label}</div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: card.color || "#e2e8f0" }}>{card.value}</div>
            </div>
            <div style={{
              width: 48, height: 48, borderRadius: "50%", background: card.iconBg,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, boxShadow: `0 0 14px ${card.iconBg}80`,
            }}>{card.icon}</div>
          </div>
        ))}
      </div>

      {/* Orders table */}
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid #334155" }}>
          <span style={{ fontSize: 16, fontWeight: "bold" }}>Orders</span>
          <span style={{ fontSize: 12, color: "#475569" }}>{filteredOrders.length} orders</span>
        </div>
        <div className="mobile-scroll-table" style={{ overflowX: "auto" }}>
          <table className="mobile-data-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#0f172a" }}>
                {[
                  { label: "ACTIONS"       },
                  { label: "TIME",         key: "time"   },
                  { label: "ACCOUNT",      key: "userId" },
                  { label: "SYMBOL",       key: "symbol" },
                  { label: "STATUS",       key: "status" },
                  { label: "TYPE"          },
                  { label: "ORDER TYPE"    },
                  { label: "QTY"           },
                  { label: "PRICE"         },
                  { label: "TRIGGER PRICE" },
                  { label: "AVG PRICE"     },
                  { label: "BROKER",       key: "broker" },
                  { label: "REMARKS"       },
                ].map(h => (
                  <th key={h.label}
                    onClick={() => h.key && (setSortBy(h.key), setSortDir(d => d === "asc" ? "desc" : "asc"))}
                    style={{
                      padding: "10px 14px", textAlign: "left", color: "#475569",
                      fontWeight: "600", fontSize: 10, letterSpacing: 1, whiteSpace: "nowrap",
                      cursor: h.key ? "pointer" : "default", borderBottom: "1px solid #334155",
                    }}
                  >
                    {h.label}
                    {h.key && sortBy === h.key && (
                      <span style={{ marginLeft: 4, color: "#3b82f6" }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="13" style={{ textAlign: "center", padding: "60px 0", color: "#475569" }}>
                    {loading ? "Loading orders..." : "No orders found"}
                  </td>
                </tr>
              ) : (
                filteredOrders.map((o, i) => {
                  const meta = STATUS_META[o.status] || STATUS_META.OPEN;
                  return (
                    <tr key={o.orderId || i} style={{ borderBottom: "1px solid #1e293b", background: i % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                      <td style={{ padding: "10px 14px" }}>
                        <button
                          onClick={() => openOrderView(o)}
                          style={{ background: "#1e3a5f", border: "none", color: "#38bdf8", padding: "3px 8px", borderRadius: 4, cursor: "pointer", fontSize: 10 }}
                        >
                          View
                        </button>
                      </td>
                      <td style={{ padding: "10px 14px", color: "#94a3b8", whiteSpace: "nowrap" }}>
                        {o.time ? new Date(o.time).toLocaleTimeString("en-IN", { hour12: false }) : "—"}
                        <div style={{ fontSize: 10, color: "#475569" }}>{o.time ? new Date(o.time).toLocaleDateString("en-IN") : ""}</div>
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <div
                          style={{
                            display: "inline-block",
                            color: getClientIdColor(o.userId),
                            background: `${getClientIdColor(o.userId)}30`,
                            border: `1px solid ${getClientIdColor(o.userId)}`,
                            boxShadow: `inset 0 0 10px ${getClientIdColor(o.userId)}25`,
                            borderRadius: 6,
                            padding: "4px 8px",
                            fontWeight: "bold",
                          }}
                        >
                          <div>{o.username}</div>
                          <div style={{ fontSize: 10 }}>{o.userId}</div>
                        </div>
                      </td>
                      <td style={{ padding: "10px 14px", fontWeight: "bold", color: "#38bdf8" }}>{o.symbol}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.color}40`, padding: "3px 8px", borderRadius: 20, fontSize: 10, fontWeight: "bold", whiteSpace: "nowrap" }}>
                          {meta.icon} {o.status}
                        </span>
                      </td>
                      <td style={{ padding: "10px 14px", color: o.type === "BUY" ? "#22c55e" : o.type === "SELL" ? "#ef4444" : "#94a3b8", fontWeight: "bold" }}>{o.type}</td>
                      <td style={{ padding: "10px 14px", color: "#94a3b8" }}>{o.orderType}</td>
                      <td style={{ padding: "10px 14px", color: "#e2e8f0", textAlign: "right" }}>{o.qty}</td>
                      <td style={{ padding: "10px 14px", color: "#e2e8f0", textAlign: "right" }}>₹{Number(o.price).toFixed(2)}</td>
                      <td style={{ padding: "10px 14px", color: "#94a3b8", textAlign: "right" }}>₹{Number(o.triggerPrice).toFixed(2)}</td>
                      <td style={{ padding: "10px 14px", color: "#e2e8f0", textAlign: "right", fontWeight: "bold" }}>₹{Number(o.avgPrice).toFixed(2)}</td>
                      <td style={{ padding: "10px 14px", color: "#94a3b8" }}>{o.broker}</td>
                      <td style={{ padding: "10px 14px", color: "#475569", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.remarks}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedOrder && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 18,
          }}
        >
          <div
            style={{
              width: "min(720px, 100%)",
              maxHeight: "92vh",
              overflowY: "auto",
              background: "#020617",
              border: "1px solid #334155",
              borderRadius: 10,
              boxShadow: "0 24px 70px rgba(0,0,0,.55)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px", borderBottom: "1px solid #334155" }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: "bold" }}>Order Details</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{selectedOrder.orderId}</div>
              </div>
              <button onClick={closeOrderView} style={{ background: "transparent", border: "1px solid #334155", color: "#e2e8f0", borderRadius: 6, padding: "6px 10px", cursor: "pointer" }}>Close</button>
            </div>

            <div style={{ padding: 18, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
              {[
                ["Account", selectedOrder.userId],
                ["Broker", selectedOrder.broker],
                ["Symbol", selectedOrder.symbol],
                ["Status", selectedOrder.status],
                ["Type", selectedOrder.type],
                ["Order Type", selectedOrder.orderType],
                ["Quantity", selectedOrder.qty],
                ["Price", `₹${Number(selectedOrder.price).toFixed(2)}`],
                ["Trigger Price", `₹${Number(selectedOrder.triggerPrice).toFixed(2)}`],
                ["Average Price", `₹${Number(selectedOrder.avgPrice).toFixed(2)}`],
                ["Time", selectedOrder.time ? new Date(selectedOrder.time).toLocaleString("en-IN") : "—"],
                ["Remarks", selectedOrder.remarks || "—"],
              ].map(([label, value]) => (
                <div key={label} style={{ border: "1px solid #1e293b", borderRadius: 8, padding: "10px 12px", background: "#0f172a" }}>
                  <div style={{ fontSize: 10, color: "#64748b", letterSpacing: 1, marginBottom: 5 }}>{label}</div>
                  <div style={{ fontSize: 13, color: "#e2e8f0", fontWeight: "bold", overflowWrap: "anywhere" }}>{value}</div>
                </div>
              ))}
            </div>

            {selectedOrder.status === "OPEN" && selectedOrder.broker === "Dhan" ? (
              <div style={{ padding: "0 18px 18px" }}>
                <div style={{ borderTop: "1px solid #334155", paddingTop: 16, marginTop: 4 }}>
                  <div style={{ fontSize: 15, fontWeight: "bold", marginBottom: 12 }}>Modify Open Order</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 10 }}>
                    <select
                      value={modifyForm.orderType}
                      onChange={e => setModifyForm(f => ({ ...f, orderType: e.target.value }))}
                      style={inp}
                    >
                      <option value="LIMIT">LIMIT</option>
                      <option value="MARKET">MARKET</option>
                      <option value="STOP_LOSS">STOP LOSS</option>
                      <option value="STOP_LOSS_MARKET">SL MARKET</option>
                    </select>
                    <input
                      value={modifyForm.quantity}
                      onChange={e => setModifyForm(f => ({ ...f, quantity: e.target.value }))}
                      placeholder="Qty"
                      type="number"
                      style={inp}
                    />
                    <input
                      value={modifyForm.price}
                      onChange={e => setModifyForm(f => ({ ...f, price: e.target.value }))}
                      placeholder="Price"
                      type="number"
                      disabled={modifyForm.orderType === "MARKET"}
                      style={{ ...inp, opacity: modifyForm.orderType === "MARKET" ? .45 : 1 }}
                    />
                    <input
                      value={modifyForm.triggerPrice}
                      onChange={e => setModifyForm(f => ({ ...f, triggerPrice: e.target.value }))}
                      placeholder="Trigger"
                      type="number"
                      disabled={!["STOP_LOSS", "STOP_LOSS_MARKET"].includes(modifyForm.orderType)}
                      style={{ ...inp, opacity: ["STOP_LOSS", "STOP_LOSS_MARKET"].includes(modifyForm.orderType) ? 1 : .45 }}
                    />
                    <select
                      value={modifyForm.validity}
                      onChange={e => setModifyForm(f => ({ ...f, validity: e.target.value }))}
                      style={inp}
                    >
                      <option value="DAY">DAY</option>
                      <option value="IOC">IOC</option>
                    </select>
                  </div>

                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
                    <button
                      onClick={cancelSelectedOrder}
                      disabled={actionLoading}
                      style={{ background: "#7f1d1d", border: "1px solid #ef4444", color: "#fecaca", borderRadius: 7, padding: "9px 14px", cursor: actionLoading ? "not-allowed" : "pointer", fontWeight: "bold" }}
                    >
                      Cancel Order
                    </button>
                    <button
                      onClick={modifySelectedOrder}
                      disabled={actionLoading}
                      className="btn-solid-green"
                      style={{ borderRadius: 7, padding: "9px 14px", cursor: actionLoading ? "not-allowed" : "pointer", fontWeight: "bold" }}
                    >
                      Modify Order
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ padding: "0 18px 18px", color: "#64748b", fontSize: 12 }}>
                This order is read-only because it is not an open Dhan order.
              </div>
            )}

            {actionMessage && (
              <div style={{ margin: "0 18px 18px", padding: "10px 12px", borderRadius: 8, border: "1px solid #334155", color: actionMessage.toLowerCase().includes("failed") ? "#fecaca" : "#bbf7d0", background: "#0f172a", fontSize: 12 }}>
                {actionMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
